import React from "react";
import { useTranslation } from "react-i18next";
import { Check, Globe } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const LANGS = [
  { code: "en", label: "English", short: "EN", badge: "EN" },
  { code: "ar", label: "العربية", short: "AR", badge: "ع" },
];

function getCurrentLanguage(language) {
  return LANGS.find((lang) => language?.startsWith(lang.code)) || LANGS[0];
}

export default function LanguageSwitcher() {
  const { t, i18n } = useTranslation();
  const current = getCurrentLanguage(i18n.language);

  return (
    <DropdownMenu dir={i18n.dir()}>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="lang-switcher"
          aria-label={`${t("common.language")}: ${current.label}`}
          className="hidden md:flex items-center gap-1 border border-border hover:border-volt/50 transition-colors rounded-sm px-3 py-2 text-xs tracking-widest uppercase font-semibold"
        >
          <Globe className="h-3.5 w-3.5" />
          {current.short}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="bg-card border border-border text-foreground rounded-sm min-w-[140px]"
      >
        <DropdownMenuRadioGroup value={current.code}>
          {LANGS.map((lang) => (
            <DropdownMenuRadioItem
              key={lang.code}
              value={lang.code}
              lang={lang.code}
              data-testid={`lang-option-${lang.code}`}
              onSelect={() => i18n.changeLanguage(lang.code)}
              className={current.code === lang.code ? "text-volt" : ""}
            >
              <span dir={lang.code === "ar" ? "rtl" : "ltr"}>{lang.label}</span>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function MobileLanguageSwitcher({ onSelect }) {
  const { t, i18n } = useTranslation();
  const current = getCurrentLanguage(i18n.language);

  const handleSelect = (code) => {
    if (code !== current.code) {
      void i18n.changeLanguage(code);
    }
    onSelect?.(code);
  };

  return (
    <section
      className="border-t border-border pt-4 mt-3"
      aria-label={t("common.language")}
      data-testid="mobile-language-switcher"
    >
      <div className="flex items-center justify-between px-1 mb-3">
        <div
          className={`flex items-center gap-2 font-bold text-muted-foreground ${
            current.code === "ar" ? "text-xs" : "text-[10px] tracking-[0.2em] uppercase"
          }`}
        >
          <Globe className="h-3.5 w-3.5" aria-hidden="true" />
          <span>{t("common.language")}</span>
        </div>
        <span
          className={`text-volt ${
            current.code === "ar" ? "text-xs" : "text-[10px] tracking-widest uppercase"
          }`}
          aria-live="polite"
        >
          {current.label}
        </span>
      </div>

      <div
        className="grid grid-cols-1 min-[360px]:grid-cols-2 gap-2"
        role="group"
        aria-label={t("common.language")}
      >
        {LANGS.map((lang) => {
          const isActive = current.code === lang.code;

          return (
            <button
              key={lang.code}
              type="button"
              lang={lang.code}
              onClick={() => handleSelect(lang.code)}
              aria-pressed={isActive}
              data-testid={`mobile-language-${lang.code}`}
              className={`min-h-12 w-full flex items-center gap-3 border px-3 py-2.5 text-sm font-semibold transition-all active:scale-[0.98] ${
                isActive
                  ? "border-volt bg-volt/10 text-foreground"
                  : "border-border bg-card/40 text-muted-foreground hover:border-foreground/30 hover:text-foreground"
              }`}
            >
              <span
                className={`flex h-7 min-w-7 items-center justify-center border px-1 text-[10px] tracking-wider ${
                  isActive ? "border-volt/50 bg-volt text-background" : "border-border text-muted-foreground"
                }`}
                aria-hidden="true"
              >
                {lang.badge}
              </span>
              <span
                className="flex-1 text-start"
                dir={lang.code === "ar" ? "rtl" : "ltr"}
              >
                {lang.label}
              </span>
              <span className="flex h-5 w-5 items-center justify-center" aria-hidden="true">
                {isActive && <Check className="h-4 w-4 text-volt" strokeWidth={2.5} />}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
